import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { AlertCircle, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";

// RFC validation: 13 chars persona_fisica, 12 chars persona_moral
function validateRFC(rfc, personType) {
  if (!rfc) return false;
  const cleaned = rfc.toUpperCase().trim();
  if (personType === "persona_moral") {
    return cleaned.length === 12 && /^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/.test(cleaned);
  }
  return cleaned.length === 13 && /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/.test(cleaned);
}

/**
 * Review phase — arrives with everything already extracted by the OCR
 * background pipeline (PhaseDocuments waits for completion before
 * letting the user continue). The user just confirms or fixes.
 */
export default function PhaseDataReview({ data, onNext, onBack, loading }) {
  const personType = data.person_type || "persona_fisica";

  // PR1.5 safeguard: fields the OCR thinks need human verification
  // (CURP/RFC pulled from a weak-precision fallback model). Tolerate
  // missing/non-array values — the prop may be absent on legacy
  // backends or on failure paths that don't expose pipeline_info.
  const reviewFieldsRaw = data.__review_fields;
  const reviewSet = new Set(
    Array.isArray(reviewFieldsRaw) ? reviewFieldsRaw.filter(Boolean) : []
  );
  const needsReview = (name) => reviewSet.has(name);

  const [formData, setFormData] = useState({
    first_name: data.first_name || "",
    paternal_surname: data.paternal_surname || "",
    maternal_surname: data.maternal_surname || "",
    birth_date: data.birth_date || "",
    curp: data.curp || "",
    rfc: data.rfc || "",
    gender: data.gender || "",
  });

  // Re-sync formData when data prop changes (e.g. user edits a doc and comes back).
  useEffect(() => {
    setFormData((prev) => ({
      first_name: prev.first_name || data.first_name || "",
      paternal_surname: prev.paternal_surname || data.paternal_surname || "",
      maternal_surname: prev.maternal_surname || data.maternal_surname || "",
      birth_date: prev.birth_date || data.birth_date || "",
      curp: prev.curp || data.curp || "",
      rfc: prev.rfc || data.rfc || "",
      gender: prev.gender || data.gender || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.first_name,
    data.paternal_surname,
    data.maternal_surname,
    data.birth_date,
    data.curp,
    data.rfc,
    data.gender,
  ]);

  const rfcExtracted = !!data.rfc;
  const [rfcEditable, setRfcEditable] = useState(!rfcExtracted);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleContinue = () => {
    if (!formData.first_name || !formData.paternal_surname || !formData.curp) {
      toast.error("Nombre, apellido paterno y CURP son obligatorios");
      return;
    }
    if (formData.curp.length !== 18) {
      toast.error("La CURP debe tener 18 caracteres");
      return;
    }
    if (!formData.rfc) {
      toast.error("El RFC es obligatorio. Si no se extrajo de la CSF, ingrésalo manualmente.");
      return;
    }
    if (!validateRFC(formData.rfc, personType)) {
      const expected = personType === "persona_moral" ? "12 caracteres" : "13 caracteres";
      toast.error(
        `El RFC no tiene formato válido (${expected} para ${
          personType === "persona_moral" ? "persona moral" : "persona física"
        })`
      );
      return;
    }
    onNext(formData);
  };

  // Detect missing fields the user must fill manually.
  const missingFields = [];
  if (!data.first_name) missingFields.push("nombre");
  if (!data.paternal_surname) missingFields.push("apellido paterno");
  if (!data.curp) missingFields.push("CURP");
  if (!data.rfc) missingFields.push("RFC");

  return (
    <div className="space-y-6" data-testid="phase-data-review">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-white mb-2">Revisión de Datos</h2>
        <p className="text-slate-400 text-sm">
          Verifica que la información sea correcta. Si algo no se extrajo, completálo manualmente.
        </p>
      </div>

      {missingFields.length > 0 && (
        <div
          className="max-w-2xl mx-auto bg-amber-500/10 border border-amber-500/30 rounded-lg p-4"
          data-testid="extraction-failed-warning"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="text-amber-400 shrink-0 mt-0.5" size={16} />
            <p className="text-amber-300 text-sm">
              No pudimos extraer estos datos automáticamente: <strong>{missingFields.join(", ")}</strong>.
              Por favor, ingrésalos manualmente.
            </p>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-slate-300 text-sm">Nombre *</Label>
            <Input
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="first-name-input"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Apellido Paterno *</Label>
            <Input
              name="paternal_surname"
              value={formData.paternal_surname}
              onChange={handleChange}
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="paternal-surname-input"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Apellido Materno</Label>
            <Input
              name="maternal_surname"
              value={formData.maternal_surname}
              onChange={handleChange}
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="maternal-surname-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300 text-sm">Fecha de Nacimiento</Label>
            <Input
              type="date"
              name="birth_date"
              value={formData.birth_date}
              onChange={handleChange}
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="birth-date-input"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Género</Label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
              data-testid="gender-select"
            >
              <option value="">Seleccionar</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300 text-sm flex items-center gap-1.5">
              CURP *
              {needsReview("curp") && (
                <span
                  className="text-xs text-amber-400 font-normal flex items-center gap-1"
                  data-testid="curp-needs-review"
                  title="OCR usó un modelo de respaldo. Verifica el valor antes de continuar."
                >
                  <AlertTriangle size={10} /> Verificar
                </span>
              )}
            </Label>
            <Input
              name="curp"
              value={formData.curp}
              onChange={handleChange}
              maxLength={18}
              placeholder="18 caracteres"
              className={
                "bg-slate-800 text-white font-mono " +
                (needsReview("curp")
                  ? "border-amber-500/60 ring-1 ring-amber-500/20"
                  : "border-slate-700")
              }
              data-testid="curp-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              {needsReview("curp")
                ? "Revisa que el CURP sea correcto (se obtuvo con un modelo de respaldo)."
                : "La constancia CURP se obtendrá automáticamente al crear el cliente"}
            </p>
          </div>
          <div>
            <Label className="text-slate-300 text-sm flex items-center gap-1.5">
              RFC *
              {rfcExtracted && !rfcEditable && !needsReview("rfc") && (
                <span className="text-xs text-teal-400 font-normal flex items-center gap-1">
                  <Lock size={10} /> Extraído de la CSF
                </span>
              )}
              {needsReview("rfc") && (
                <span
                  className="text-xs text-amber-400 font-normal flex items-center gap-1"
                  data-testid="rfc-needs-review"
                  title="OCR usó un modelo de respaldo. Verifica el RFC antes de continuar."
                >
                  <AlertTriangle size={10} /> Verificar
                </span>
              )}
            </Label>

            {rfcExtracted && !rfcEditable && !needsReview("rfc") ? (
              <div className="space-y-1">
                <div className="relative">
                  <Input
                    name="rfc"
                    value={formData.rfc}
                    readOnly
                    className="bg-slate-800/50 border-teal-500/30 text-white font-mono uppercase cursor-not-allowed"
                    data-testid="rfc-input-readonly"
                  />
                  <Lock
                    size={14}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-400/50"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setRfcEditable(true)}
                  className="text-xs text-amber-400 hover:text-amber-300 underline"
                  data-testid="rfc-edit-btn"
                >
                  ¿El RFC es incorrecto? Haz clic para corregirlo
                </button>
              </div>
            ) : (
              <Input
                name="rfc"
                value={formData.rfc}
                onChange={handleChange}
                maxLength={13}
                placeholder={personType === "persona_moral" ? "12 caracteres" : "13 caracteres"}
                className={
                  "bg-slate-800 text-white font-mono uppercase " +
                  (needsReview("rfc")
                    ? "border-amber-500/60 ring-1 ring-amber-500/20"
                    : "border-slate-700")
                }
                data-testid="rfc-input"
              />
            )}
          </div>
        </div>
      </div>

      {/* PR1.5 — banner global cuando hay campos por verificar */}
      {reviewSet.size > 0 && (
        <div
          className="mt-6 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm flex items-start gap-2"
          data-testid="needs-review-banner"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <strong className="font-semibold">Verifica antes de continuar.</strong>{" "}
            El OCR usó un modelo de respaldo para{" "}
            <code className="font-mono">
              {Array.from(reviewSet).join(", ")}
            </code>
            . Revisa el valor; un error en CURP/RFC quedaría en el expediente.
          </div>
        </div>
      )}

      <div className="flex justify-between mt-8">
        <Button
          onClick={onBack}
          variant="outline"
          className="border-slate-700"
          data-testid="back-btn"
        >
          ← Atrás
        </Button>
        <Button
          onClick={handleContinue}
          disabled={loading}
          className="bg-teal-600 hover:bg-teal-500 text-white"
          data-testid="continue-btn"
        >
          Continuar →
        </Button>
      </div>
    </div>
  );
}
